import React, { useContext, useEffect, useRef } from 'react'

import { StoreContext } from '@/context/context'
import { StoreContextType, IConfigObj, IUserObj } from '@/context/types'
import Conversations from './Conversations'
import Messages from './Messages'
import Config from './Config'
import Errors from './Errors'
import Input from './Input'
import Loading from './Loading'
import {
  CONVERSATION_STATE,
  DEFAULT_HEARTBEAT_INTERVAL,
  MESSAGE_TYPES,
} from '@/context/constants'

export const useMessenger = (
  context: React.Context<StoreContextType | null> = StoreContext,
) => {
  const store = useContext(context) as StoreContextType

  const intervalId = useRef<NodeJS.Timer>()
  const { getConfig, setConfig } = Config(context)
  const { getErrors, setErrors, clearErrors } = Errors(context)
  const {
    getConversationList,
    setConversationList,
    getSelectedConversation,
    setSelectedConversation,
    findConversation,
    getConversationState,
    removeConversation,
  } = Conversations(context)
  const {
    getConversationMessages,
    setConversationMessages,
    getLastConversationMessage,
    MESSAGE_HANDLERS,
    getForceUpdate,
    setForceUpdate,
  } = Messages(context)

  const {
    getDrafts,
    setDrafts,
    getInputMessage,
    setInputMessage,
    sendMessage,
    getSendTo,
    setSendTo,
  } = Input(context)
  const { getLoading, setLoading } = Loading(context)
  const { introMessage, setIntroMessage } = store.intro || {}

  const conversationList = getConversationList()
  const forceUpdate = getForceUpdate()

  const isConversationNew =
    getConversationState(getSelectedConversation()) === CONVERSATION_STATE.NEW

  const getIsInit = () => store?.init.isInit
  const setIsInit = (isInit: boolean) => store.init.setIsInit(isInit)

  const getWSInstance = () => store?.websocket.instance
  const setWSInstance = (wsInstance: WebSocket | null) =>
    store?.websocket.setInstance(wsInstance)

  const getHasUnread = () => store.unread.hasUnread

  const getIsTyping = () => store?.typing.isTyping
  const setIsTyping = (isTyping: boolean) => store.typing.setIsTyping(isTyping)

  const getUserObj = () => store?.name.userObj
  const setUserObj = (user: IUserObj) => store?.name.setUserObj(user)

  const getFaqBody = () => store.faq.faqBody
  const setFaqBody = (body: string) => store.faq.setFaqBody(body)

  const wsInstance = getWSInstance()

  const initialize = (config: IConfigObj) => {
    setIsInit(false)
    const { wsURL, source, token, userExternalId, userType } = config

    const params = new URLSearchParams()
    token && params.set('token', token)
    params.set('source', source)
    userType && params.set('userType', userType)
    params.set('userExternalId', userExternalId)

    const url = [wsURL, params.toString()].join('?')
    const ws = new WebSocket(url)

    if (!ws) {
      return null
    }

    setConfig(config)
    setWSInstance(ws)

    return ws
  }

  useEffect(() => {
    const config = getConfig()
    if (!wsInstance || !config) {
      return
    }

    wsInstance.onopen = () => {
      console.log('WEBSOCKET CONNECTED')

      // Do heartbeat message every interval in order to keep the connection alive while idle
      setInterval(() => {
        sendMessage({
          type: MESSAGE_TYPES.HEARTBEAT,
        })
      }, config?.hearbeatInterval || DEFAULT_HEARTBEAT_INTERVAL)
    }

    wsInstance.onmessage = e => {
      const wsMessage = JSON.parse(e.data)

      if (wsMessage.errors) {
        setErrors(wsMessage.type, wsMessage.errors)
        console.error(`WEBSOCKET ERROR: ${wsMessage.type}`, wsMessage.errors)
        return
      }

      // Handle ok cases
      MESSAGE_HANDLERS[wsMessage.type](wsMessage)
    }

    wsInstance.onclose = () => {
      console.log('WEBSOCKET DISCONNECTED')
      clearInterval(intervalId.current as NodeJS.Timeout)
    }
  }, [wsInstance, store])

  useEffect(() => {
    if (conversationList?.length > 0) {
      const conversation = conversationList.find(
        convo =>
          convo.metadata?.isRead === false &&
          convo.metadata?.state !== CONVERSATION_STATE.CLOSED,
      )

      if (conversation) {
        store.unread.setHasUnread(true)
      } else {
        store.unread.setHasUnread(false)
      }
    }
  }, [conversationList, forceUpdate])

  return {
    getConfig,
    setConfig,
    initialize,
    getIsInit,
    setIsInit,
    getConversationList,
    setConversationList,
    getConversationMessages,
    setConversationMessages,
    getSelectedConversation,
    setSelectedConversation,
    getConversationState,
    removeConversation,
    isConversationNew,
    getLastConversationMessage,
    findConversation,
    getWSInstance,
    setWSInstance,
    getInputMessage,
    setInputMessage,
    sendMessage,
    getErrors,
    clearErrors,
    getLoading,
    setLoading,
    introMessage,
    setIntroMessage,
    getUserObj,
    setUserObj,
    getHasUnread,
    forceUpdate,
    setForceUpdate,
    getIsTyping,
    setIsTyping,
    getSendTo,
    setSendTo,
    getFaqBody,
    setFaqBody,
    getDrafts,
    setDrafts,
  }
}

export default useMessenger
